import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { next } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { Service } from 'webapp-types';
import { BillingFeature, UserAccess } from 'webapp/enums/enums';
import { getPrettyModelName } from 'webapp/utils/model';

import highlightResult from 'webapp/helpers/highlight-result';
import AvatarOrInitialsComponent from 'webapp/components/avatar-or-initials';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import FormRadiobuttonGroupComponent from 'webapp/components/form/radiobutton-group';
import type { SelectableListItem } from 'webapp/components/selectable-list';
import SelectableListComponent from 'webapp/components/selectable-list';

import type { Board, BoardLoop, Goal } from 'webapp/models-custom';

interface Args {
	model: Board | BoardLoop | Goal;
	onChange: (access: UserAccess, users: LabelValuePairNumeric[]) => void;
	introText?: string;
	onContentChange?: () => void;
}

export default class UserAccessPermissionsComponent extends Component<Args> {
	@service('model-manager/users') declare mmUsersService: Service.ModelManager.Users;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('billing') declare billingService: Service.Billing;
	@service('account') declare accountService: Service.Account;

	@tracked selectedAccess!: UserAccess;

	@tracked usersWithPermission: LabelValuePairNumeric[] = [];

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.selectedAccess = this.args.model.access;
		this.usersWithPermission = this.usersList.filter((user) =>
			(this.args.model.users || []).includes(user.value),
		);

		next(() => {
			this.args.onChange(this.selectedAccess, this.usersWithPermission);
		});
	}

	get introText() {
		if (this.args.introText) {
			return this.args.introText;
		} else {
			const itemName = getPrettyModelName(this.args.model);
			return `This ${itemName} is accessible to:`;
		}
	}

	get showUsersTable() {
		return this.selectedAccess === UserAccess.RESTRICTED;
	}

	get showPrivateInfo() {
		return this.selectedAccess === UserAccess.PRIVATE;
	}

	get showPublicInfo() {
		return this.selectedAccess === UserAccess.PUBLIC;
	}

	get showLearnMoreLink() {
		return !this.accountService.hideDataboxNotWhiteLabeledContentForNonAdmins;
	}

	get options() {
		const options = [];

		options.push({
			value: UserAccess.PUBLIC,
			label: 'All users',
		});

		let restrictedLabel = 'Selected users';
		if (this.selectedAccess === UserAccess.RESTRICTED) {
			restrictedLabel += ` (${this.usersWithPermission.length})`;
		}

		options.push({
			value: UserAccess.RESTRICTED,
			label: restrictedLabel,
		});

		const privateTooltip = !this.args.model.isMine
			? 'Only the creator can change the privacy setting to private.'
			: '';
		options.push({
			value: UserAccess.PRIVATE,
			label: 'Only you (private) <ui-icon name="Lock"></ui-icon>',
			// label2: 'Only visible to you.',
			tooltipText: privateTooltip,
			disabled: !this.args.model.isMine,
		});

		return options;
	}

	get usersList() {
		return this.mmUsersService.activatedUserRecords.map((user) => {
			const numericId = +user.id!;
			return {
				value: numericId,
				label: user.name,
				email: user.email,
				avatar: user.avatar,
				isDisabled:
					numericId === +(this.args.model as Board | BoardLoop).createdById ||
					numericId === this.currentUserService.numericId,
			};
		});
	}

	@action
	changeSelectedAccess(value: AnySimpleValue) {
		const selectedAccess = value as UserAccess;
		this.selectedAccess = selectedAccess;
		this.args.onChange(selectedAccess, this.usersWithPermission);
		this.args.onContentChange?.();
	}

	@action
	changeUserSelection(user: SelectableListItem, isSelected: boolean) {
		if (isSelected) {
			if (!this.usersWithPermission.find((u) => u.value === user.value)) {
				this.updateUsersWithPermission([
					...this.usersWithPermission,
					user as LabelValuePairNumeric,
				]);
			}
		} else {
			this.updateUsersWithPermission(
				this.usersWithPermission.filter((u) => u.value !== user.value),
			);
		}
	}

	@action
	selectAll() {
		this.updateUsersWithPermission(this.usersList);
	}

	@action
	deselectAll() {
		this.updateUsersWithPermission(this.usersList.filter((u) => u.isDisabled));
	}

	@action
	addNewUser() {
		if (this.currentUserService.isVerified) {
			if (this.billingService.checkIfCan(BillingFeature.USERS)) {
				this.mmUsersService.openUserInvitePopup({
					onUserInvited: (userRecords) => {
						userRecords.forEach((user) => {
							this.usersList.push({
								value: +user.id,
								label: user.name,
								email: user.email,
								avatar: user.avatar,
								isDisabled: false,
							});
						});
					},
				});
			} else {
				this.billingService.handleLimitations(BillingFeature.USERS);
			}
		} else {
			this.currentUserService.openEmailVerificationInfoPopup('invite new users');
		}
	}

	private updateUsersWithPermission(users: LabelValuePairNumeric[]) {
		this.usersWithPermission = users;
		this.args.onChange(this.selectedAccess, users);
	}
	<template>
		<div class="row user-access-permissions">

			<p class="intro-text">{{this.introText}}</p>

			<div class="user-access-choice selection">

				<FormRadiobuttonGroupComponent
					class="radiobutton-default"
					@name="permissions"
					@selected={{this.selectedAccess}}
					@options={{this.options}}
					@onChange={{this.changeSelectedAccess}}
				/>
			</div>

			{{#if this.showUsersTable}}
				<div class="user-access-table">
					<SelectableListComponent
						@options={{this.usersList}}
						@values={{this.usersWithPermission}}
						@onItemSelectionChanged={{this.changeUserSelection}}
						as |s|
					>
						<s.list
							@onSelectAll={{this.selectAll}}
							@onDeselectAll={{this.deselectAll}}
							as |list|
						>
							<list.row as |row|>
								<AvatarOrInitialsComponent
									@name={{row.item.label}}
									@avatar={{row.item.avatar}}
								/>
								{{highlightResult text=row.item.label match=row.typedFilter}}
								<span class="email">
									{{highlightResult text=row.item.email match=row.typedFilter}}
								</span>
							</list.row>
							<list.lastRow>
								<div
									role="button"
									class="new-item-add"
									{{on "click" this.addNewUser}}
								>
									<CommonUiIconComponent @name="Plus" @size="xl" />Add user ...
								</div>
							</list.lastRow>
						</s.list>
					</SelectableListComponent>
				</div>
			{{else if this.showPrivateInfo}}
				<div class="extra-info">
					If your user account is being deleted, private dashboards are deleted as well.
				</div>
			{{else if this.showPublicInfo}}
				<div class="extra-info">
					It can be edited by
					<strong>Admins</strong>
					and
					<strong>Editors</strong>, and viewed by
					<strong>Users</strong>
					and
					<strong>Viewers</strong>.
					<br /><br />
					{{#if this.showLearnMoreLink}}
						<a
							href="https://help.databox.com/overview-user-management#user-roles-defined"
							target="_blank"
						>
							<span>Learn more about user roles</span>
							<CommonUiIconComponent @name="External" @size="sm" />
						</a>
					{{/if}}
				</div>
			{{/if}}
		</div>
	</template>
}
